import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Styled Components
const FAQSection = styled.section`
  padding: 5rem 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const AccordionItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  background-color: white;
`;

const AccordionHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  background-color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  &:focus {
    outline: none;
  }
`;

const IconWrapper = styled.div`
  color: #1e3a8a;
  font-size: 1rem;
  transition: transform 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: ${props => props.isOpen ? '0 1.25rem 1.25rem' : '0 1.25rem'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => props.isOpen ? '1' : '0'};
`;

const Answer = styled.p`
  color: #4b5563;
  line-height: 1.6;
`;

// Sample FAQ data - replace with Firebase data in production
const sampleFAQs = [
  {
    id: 1,
    question: 'What are the check-in and check-out times?',
    answer: 'Check-in time is 2:00 PM and check-out time is 11:00 AM. Early check-in or late check-out may be available upon request, subject to availability.'
  },
  {
    id: 2,
    question: 'Is breakfast included in the room rate?',
    answer: 'Breakfast is complimentary for all guests staying in our Premium Cottages. For other room types, breakfast can be added at an additional cost of ₹500 per person per day.'
  },
  {
    id: 3,
    question: 'Do you offer airport transfers?',
    answer: 'Yes, we offer airport transfers at an additional cost. Please provide your flight details at the time of booking or contact our concierge at least 24 hours in advance to arrange transportation.'
  },
  {
    id: 4,
    question: 'Is Wi-Fi available at the resort?',
    answer: 'Yes, complimentary high-speed Wi-Fi is available throughout the property, including all guest rooms and public areas.'
  },
  {
    id: 5,
    question: 'Are pets allowed at the resort?',
    answer: 'We regret to inform that pets are not allowed at our resort, with the exception of service animals.'
  },
  {
    id: 6,
    question: 'What activities are available at the resort?',
    answer: 'We offer a variety of activities including shikara rides on Wular Lake, guided trekking tours, fishing experiences, bonfire evenings, and more. Our concierge can help you plan and book these activities during your stay.'
  }
];

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Fetch FAQs from Firebase
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const faqCollection = collection(db, 'faqs');
        const faqSnapshot = await getDocs(faqCollection);
        
        if (!faqSnapshot.empty) {
          const faqList = faqSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFaqs(faqList);
        } else {
          // Use sample data if no FAQs in Firebase
          setFaqs(sampleFAQs);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Fallback to sample data
        setFaqs(sampleFAQs);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFAQs();
  }, []);
  
  // Toggle accordion item
  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  return (
    <FAQSection id="faq">
      <SectionTitle>Frequently Asked Questions</SectionTitle>
      <FAQContainer>
        {loading ? (
          <p>Loading FAQs...</p>
        ) : (
          faqs.map((faq, index) => (
            <AccordionItem key={faq.id}>
              <AccordionHeader onClick={() => toggleAccordion(index)}>
                {faq.question}
                <IconWrapper>
                  {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                </IconWrapper>
              </AccordionHeader>
              <AccordionContent isOpen={activeIndex === index}>
                <Answer>{faq.answer}</Answer>
              </AccordionContent>
            </AccordionItem>
          ))
        )}
      </FAQContainer>
    </FAQSection>
  );
};

export default FAQ;
